/**
 * Toaster store — global queue of toast notifications.
 *
 * Mirrors the legacy `app-toaster` shell component
 * (`raptor-shell-ui/src/app/shared/components/toaster`), which subscribed
 * to a `ToasterService` and rendered `<ey-messaging-alerts>`. Here we
 * back the same surface with a Zustand queue + `<MotifToast>` items
 * inside a single `<MotifToaster>` slot mounted in `ShellLayout`.
 *
 * MFEs and non-React callers can push via the imperative `pushToast()`
 * helper; the M2.9 shim re-exports it as `window.EY_RAPToR_showToast`
 * (added in this commit so legacy MFE alerts have somewhere to land).
 */

import { create } from 'zustand';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface ToastItem {
  id: number;
  message: string;
  /** Optional short title above the message. */
  title?: string;
  variant: ToastVariant;
  /** Auto-dismiss after `timeoutMs` (default 5000); pass 0 to keep open. */
  timeoutMs: number;
}

interface ToastState {
  items: ToastItem[];
  push: (input: Omit<ToastItem, 'id' | 'timeoutMs'> & { timeoutMs?: number }) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;

export const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (input) => {
    const id = nextId++;
    const item: ToastItem = {
      id,
      message: input.message,
      title: input.title,
      variant: input.variant,
      timeoutMs: input.timeoutMs ?? 5000,
    };
    set((s) => ({ items: [...s.items, item] }));
    return id;
  },
  dismiss: (id) =>
    set((s) => ({
      items: s.items.filter((t) => t.id !== id),
    })),
}));

/** Imperative helper for non-React callers (HTTP layer, MFE bridge). */
export function pushToast(input: Parameters<ToastState['push']>[0]): number {
  return useToastStore.getState().push(input);
}

export function dismissToast(id: number): void {
  useToastStore.getState().dismiss(id);
}
